import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Mic, MicOff, Paperclip, X, FileImage, Loader2, Trash2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../lib/supabase';
import { startRecording, transcribeAudio } from '../services/transcriptionService';
import { processReceiptImage, isFileSupported, getFileErrorMessage } from '../services/fileProcessingService';

type Message = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  createdAt: string;
  attachments?: {
    type: 'image' | 'file';
    name: string;
    url?: string;
    data?: any;
  }[];
};

type BalanceSnapshot = {
  current_balance: number;
  total_income: number;
  total_expenses: number;
  total_goal_contributions: number;
  available_balance: number;
  last_updated: string;
};

type ChatHistoryMessage = {
  role: 'user' | 'assistant';
  content: string;
};

type ChatConversation = {
  id: string;
  title: string;
  updatedAt: string;
  messages: Message[];
};

type PersistedAssistenteIAState = {
  activeConversationId: string;
  conversations: ChatConversation[];
};

const createDefaultSystemMessage = (): Message => ({
  id: `sys-${Date.now()}`,
  role: 'system',
  text: 'Sou a Bia, Assistente com IA da Grana em Dia. Escreva sua pergunta abaixo e pressione Enviar.',
  createdAt: new Date().toISOString()
});

const deriveConversationTitle = (messages: Message[]): string => {
  const firstUserMessage = messages.find((message) => message.role === 'user' && message.text.trim().length > 0);
  if (!firstUserMessage) return 'Nova conversa';
  return firstUserMessage.text.replace(/\s+/g, ' ').trim().slice(0, 48);
};

const normalizeSessionId = (value: string) => value.replace(/\D/g, '');
const chatBiaFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/chat-bia`;

async function invokeChatBiaFallback(payload: Record<string, unknown>) {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (import.meta.env.VITE_SUPABASE_ANON_KEY) {
    headers.apikey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  }

  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }

  const response = await fetch(chatBiaFunctionUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  const rawBody = await response.text();

  if (!response.ok) {
    throw new Error(`chat-bia HTTP ${response.status}: ${rawBody}`);
  }

  try {
    return JSON.parse(rawBody);
  } catch {
    throw new Error(`chat-bia retornou JSON inválido: ${rawBody}`);
  }
}

const AssistenteIA: React.FC = () => {
  const conversationIdRef = useRef(`conv-${crypto.randomUUID()}`);
  const chatHydratedRef = useRef(false);
  const [messages, setMessages] = useState<Message[]>([createDefaultSystemMessage()]);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState('');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [processingFile, setProcessingFile] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [userWhatsapp, setUserWhatsapp] = useState('');
  const [balanceSnapshot, setBalanceSnapshot] = useState<BalanceSnapshot | null>(null);
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const stopRecordingRef = useRef<(() => void) | null>(null);
  const chatStorageKey = userId ? `assistente-ia-chat:${userId}` : '';

  useEffect(() => {
    if (!chatStorageKey) return;

    try {
      const rawState = localStorage.getItem(chatStorageKey);
      if (rawState) {
        const parsedState = JSON.parse(rawState) as Partial<PersistedAssistenteIAState> & {
          conversationId?: string;
          messages?: Message[];
        };

        let restoredConversations: ChatConversation[] = [];
        let restoredActiveConversationId = '';

        if (Array.isArray(parsedState?.conversations) && parsedState.conversations.length > 0) {
          restoredConversations = parsedState.conversations;
          restoredActiveConversationId = parsedState.activeConversationId || parsedState.conversations[0].id;
        } else if (Array.isArray(parsedState?.messages) && parsedState.messages.length > 0) {
          const migratedId = parsedState.conversationId || `conv-${crypto.randomUUID()}`;
          restoredConversations = [
            {
              id: migratedId,
              title: deriveConversationTitle(parsedState.messages),
              updatedAt: new Date().toISOString(),
              messages: parsedState.messages,
            },
          ];
          restoredActiveConversationId = migratedId;
        }

        if (restoredConversations.length === 0) {
          const fallbackId = `conv-${crypto.randomUUID()}`;
          restoredConversations = [
            {
              id: fallbackId,
              title: 'Nova conversa',
              updatedAt: new Date().toISOString(),
              messages: [createDefaultSystemMessage()],
            },
          ];
          restoredActiveConversationId = fallbackId;
        }

        const activeConversation = restoredConversations.find(
          (conversation) => conversation.id === restoredActiveConversationId
        ) || restoredConversations[0];

        conversationIdRef.current = activeConversation.id;
        setConversations(restoredConversations);
        setActiveConversationId(activeConversation.id);
        setMessages(activeConversation.messages);
      } else {
        const firstConversationId = `conv-${crypto.randomUUID()}`;
        const firstConversation: ChatConversation = {
          id: firstConversationId,
          title: 'Nova conversa',
          updatedAt: new Date().toISOString(),
          messages: [createDefaultSystemMessage()],
        };

        conversationIdRef.current = firstConversationId;
        setConversations([firstConversation]);
        setActiveConversationId(firstConversationId);
        setMessages(firstConversation.messages);
      }
    } catch (error) {
      console.warn('Falha ao restaurar histórico local do chat da Bia:', error);
      const fallbackConversationId = `conv-${crypto.randomUUID()}`;
      const fallbackConversation: ChatConversation = {
        id: fallbackConversationId,
        title: 'Nova conversa',
        updatedAt: new Date().toISOString(),
        messages: [createDefaultSystemMessage()],
      };

      conversationIdRef.current = fallbackConversationId;
      setConversations([fallbackConversation]);
      setActiveConversationId(fallbackConversationId);
      setMessages(fallbackConversation.messages);
    } finally {
      chatHydratedRef.current = true;
    }
  }, [chatStorageKey]);

  useEffect(() => {
    if (!chatHydratedRef.current || !activeConversationId) return;

    setConversations((previousConversations) => {
      const now = new Date().toISOString();
      const updatedConversations = previousConversations.map((conversation) => {
        if (conversation.id !== activeConversationId) return conversation;
        return {
          ...conversation,
          title: deriveConversationTitle(messages),
          updatedAt: now,
          messages,
        };
      });

      const conversationExists = updatedConversations.some(
        (conversation) => conversation.id === activeConversationId
      );

      if (conversationExists) return updatedConversations;

      return [
        ...updatedConversations,
        {
          id: activeConversationId,
          title: deriveConversationTitle(messages),
          updatedAt: now,
          messages,
        },
      ];
    });
  }, [activeConversationId, messages]);

  useEffect(() => {
    if (!chatStorageKey || !chatHydratedRef.current) return;

    try {
      const stateToPersist: PersistedAssistenteIAState = {
        activeConversationId,
        conversations,
      };

      localStorage.setItem(chatStorageKey, JSON.stringify(stateToPersist));
    } catch (error) {
      console.warn('Falha ao persistir histórico local do chat da Bia:', error);
    }
  }, [activeConversationId, chatStorageKey, conversations]);

  const selectConversation = (conversationId: string) => {
    const selectedConversation = conversations.find((conversation) => conversation.id === conversationId);
    if (!selectedConversation) return;

    conversationIdRef.current = selectedConversation.id;
    setActiveConversationId(selectedConversation.id);
    setMessages(selectedConversation.messages);
    setInput('');
    setAttachedFiles([]);
  };

  const createNewConversation = () => {
    const newConversationId = `conv-${crypto.randomUUID()}`;
    const newConversation: ChatConversation = {
      id: newConversationId,
      title: 'Nova conversa',
      updatedAt: new Date().toISOString(),
      messages: [createDefaultSystemMessage()],
    };

    conversationIdRef.current = newConversationId;
    setConversations((previousConversations) => [newConversation, ...previousConversations]);
    setActiveConversationId(newConversationId);
    setMessages(newConversation.messages);
    setInput('');
    setAttachedFiles([]);
    inputRef.current?.focus();
  };

  const deleteConversation = (conversationId: string) => {
    const targetConversation = conversations.find((conversation) => conversation.id === conversationId);
    if (!targetConversation) return;

    const shouldDelete = window.confirm(`Excluir a conversa "${targetConversation.title}"?`);
    if (!shouldDelete) return;

    const remainingConversations = conversations.filter((conversation) => conversation.id !== conversationId);

    if (remainingConversations.length === 0) {
      const fallbackConversationId = `conv-${crypto.randomUUID()}`;
      const fallbackConversation: ChatConversation = {
        id: fallbackConversationId,
        title: 'Nova conversa',
        updatedAt: new Date().toISOString(),
        messages: [createDefaultSystemMessage()],
      };

      conversationIdRef.current = fallbackConversationId;
      setConversations([fallbackConversation]);
      setActiveConversationId(fallbackConversationId);
      setMessages(fallbackConversation.messages);
      setInput('');
      setAttachedFiles([]);
      return;
    }

    setConversations(remainingConversations);

    if (conversationId === activeConversationId) {
      const nextConversation = [...remainingConversations].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )[0];

      conversationIdRef.current = nextConversation.id;
      setActiveConversationId(nextConversation.id);
      setMessages(nextConversation.messages);
      setInput('');
      setAttachedFiles([]);
    }
  };

  const formatCurrency = (value: number) =>
    Number.isFinite(value)
      ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '—';

  // extrair email e uuid do Supabase na montagem
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        if (data?.user) {
          const email = data.user.email || '';
          const uuid = data.user.id || '';
          
          // Buscar WhatsApp do perfil
          const { data: profileData } = await supabase
            .from('profiles')
            .select('whatsapp')
            .eq('user_id', uuid)
            .single();
          
          const whatsapp = profileData?.whatsapp || '';
          
          console.log('✅ Email extraído:', email);
          console.log('✅ UUID extraído:', uuid);
          console.log('✅ WhatsApp extraído:', whatsapp);

          setUserEmail(email);
          setUserId(uuid);
          setUserWhatsapp(whatsapp);
        }
      } catch (err) {
        console.error('❌ Erro ao extrair dados do Supabase:', err);
      }
    };
    fetchUserData();
  }, []);

  const fetchBalanceSnapshot = useCallback(async () => {
    if (!userId || isBalanceLoading) return balanceSnapshot;
    setIsBalanceLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_balance')
        .select(
          'current_balance,total_income,total_expenses,total_goal_contributions,available_balance,last_updated'
        )
        .eq('user_id', userId)
        .single();

      if (error) {
        console.warn('⚠️ Não foi possível carregar saldo atual para IA:', error.message);
        return balanceSnapshot;
      }

      if (data) {
        const snapshot = {
          current_balance: Number(data.current_balance) || 0,
          total_income: Number(data.total_income) || 0,
          total_expenses: Number(data.total_expenses) || 0,
          total_goal_contributions: Number(data.total_goal_contributions) || 0,
          available_balance: Number(data.available_balance) || 0,
          last_updated: data.last_updated
        } satisfies BalanceSnapshot;

        setBalanceSnapshot(snapshot);
        return snapshot;
      }
    } catch (err) {
      console.error('❌ Erro ao buscar saldo atual para IA:', err);
    } finally {
      setIsBalanceLoading(false);
    }
    return balanceSnapshot;
  }, [balanceSnapshot, isBalanceLoading, userId]);

  // Buscar saldo atual para fornecer contexto à IA (evita respostas desatualizadas)
  useEffect(() => {
    fetchBalanceSnapshot();
  }, [fetchBalanceSnapshot]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if ((!text && attachedFiles.length === 0) || !userEmail || !userId) {
      console.warn('⚠️ Mensagem, email ou uuid ausentes');
      return;
    }

    // Processar arquivos anexados primeiro
    let imageDescription = '';
    let userMsgText = text;
    
    if (attachedFiles.length > 0) {
      setProcessingFile(true);
      try {
        const file = attachedFiles[0];
        const result = await processReceiptImage(file);
        
        // Criar descrição para exibir no chat
        const extractedInfo = `\n\n📎 **Comprovante anexado:**\n- Texto: ${result.extractedText || 'N/A'}\n- Valor: ${result.amount ? `R$ ${result.amount}` : 'N/A'}\n- Data: ${result.date || 'N/A'}\n- Estabelecimento: ${result.merchant || 'N/A'}\n- Categoria: ${result.category || 'N/A'}`;
        
        userMsgText = text ? `${text}\n${extractedInfo}` : extractedInfo;
        
        // Preparar descrição estruturada para enviar ao N8N
        imageDescription = `Imagem anexada com dados extraídos:\n- Valor: ${result.amount || 'não detectado'}\n- Data: ${result.date || 'não detectada'}\n- Estabelecimento: ${result.merchant || 'não identificado'}\n- Categoria sugerida: ${result.category || 'não determinada'}\n- Tipo: ${result.type || 'não especificado'}\n- Texto detectado: ${result.extractedText || 'nenhum'}`;
        
        // Exibir mensagem do usuário com a imagem processada
        const userMsg: Message = { 
          id: `u-${Date.now()}`, 
          role: 'user', 
          text: userMsgText,
          createdAt: new Date().toISOString(),
          attachments: [{
            type: 'image',
            name: file.name,
            data: result
          }]
        };
        setMessages(prev => [...prev, userMsg]);
        
      } catch (error: any) {
        const errMsg: Message = { 
          id: `e-${Date.now()}`, 
          role: 'assistant', 
          text: `❌ Erro ao processar arquivo: ${error.message}`, 
          createdAt: new Date().toISOString() 
        };
        setMessages(prev => [...prev, errMsg]);
        setProcessingFile(false);
        setAttachedFiles([]);
        return;
      } finally {
        setProcessingFile(false);
        setAttachedFiles([]);
      }
    } else {
      // Sem arquivo, apenas texto
      const userMsg: Message = { 
        id: `u-${Date.now()}`, 
        role: 'user', 
        text, 
        createdAt: new Date().toISOString() 
      };
      setMessages(prev => [...prev, userMsg]);
    }

    setInput('');
    setLoading(true);

    try {
      // Garantir que o saldo mais recente esteja disponível antes de enviar
      const latestBalance = balanceSnapshot || (await fetchBalanceSnapshot()) || null;

      // Construir mensagem final para o N8N
      // Se tem imagem, inclui a descrição. Se tem texto, inclui também.
      const baseMessage = imageDescription && text
        ? `${text}\n\n${imageDescription}`
        : imageDescription || text;

      const balanceContext = latestBalance
        ? `\n\n[Contexto financeiro atual — use sempre estes valores nas respostas]\n- Saldo atual (na tela): ${formatCurrency(
            latestBalance.current_balance
          )}\n- Saldo disponível (após metas): ${formatCurrency(
            latestBalance.available_balance
          )}\n- Receitas acumuladas: ${formatCurrency(
            latestBalance.total_income
          )}\n- Despesas acumuladas: ${formatCurrency(
            latestBalance.total_expenses
          )}\n- Contribuições em metas: ${formatCurrency(
            latestBalance.total_goal_contributions
          )}\n- Última atualização: ${
            latestBalance.last_updated
              ? new Date(latestBalance.last_updated).toLocaleString('pt-BR')
              : '—'
          }\nResponda perguntas sobre saldo/receitas/despesas somente com esses números e destaque o Saldo atual quando solicitado.`
        : '';

      const finalMessage = balanceContext ? `${baseMessage}\n${balanceContext}` : baseMessage;

      const history: ChatHistoryMessage[] = messages
        .filter((message) => message.role === 'user' || message.role === 'assistant')
        .map((message) => ({
          role: message.role as ChatHistoryMessage['role'],
          content: message.text
        }));

      const payload = {
        message: finalMessage,
        userId,
        history,
        sessionId: normalizeSessionId(userWhatsapp) || conversationIdRef.current,
        conversationId: conversationIdRef.current,
        source: 'assistente-ia'
      };

      let data: { response?: string } | null = null;

      try {
        const invokeResult = await supabase.functions.invoke('chat-bia', {
          body: payload
        });

        if (invokeResult.error) {
          throw invokeResult.error;
        }

        data = invokeResult.data;
      } catch (invokeError: any) {
        console.error('❌ Erro da Edge Function chat-bia via invoke:', invokeError);

        if (invokeError?.message === 'Failed to fetch') {
          console.warn('⚠️ Tentando fallback com fetch direto para a Edge Function...');
          data = await invokeChatBiaFallback(payload);
        } else {
          throw invokeError;
        }
      }

      const assistantText = data?.response || 'Resposta recebida';

      const assistantMsg: Message = { 
        id: `a-${Date.now()}`, 
        role: 'assistant', 
        text: assistantText, 
        createdAt: new Date().toISOString() 
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err: any) {
      const isNetworkError = err?.message === 'Failed to fetch';
      const friendlyMessage = isNetworkError
        ? `❌ Erro de rede ao chamar a Edge Function. Verifique se chat-bia está deployada no mesmo projeto de ${chatBiaFunctionUrl} e se VITE_SUPABASE_URL aponta para esse projeto.`
        : `❌ Erro ao enviar mensagem: ${err.message}`;

      const errMsg: Message = { 
        id: `e-${Date.now()}`, 
        role: 'assistant', 
        text: friendlyMessage,
        createdAt: new Date().toISOString() 
      };
      setMessages(prev => [...prev, errMsg]);
      console.error('❌ Erro no AssistenteIA:', err);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enter ou Cmd+Enter para enviar
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleRecording = async () => {
    if (isRecording) {
      // Parar gravação
      if (stopRecordingRef.current) {
        stopRecordingRef.current();
        stopRecordingRef.current = null;
      }
      setIsRecording(false);
    } else {
      // Iniciar gravação
      try {
        setIsRecording(true);
        const stopFn = await startRecording(async (audioBlob) => {
          setIsTranscribing(true);
          try {
            const result = await transcribeAudio(audioBlob);
            setInput(prev => prev + (prev ? '\n\n' : '') + result.text);
            inputRef.current?.focus();
          } catch (error: any) {
            const errMsg: Message = { 
              id: `e-${Date.now()}`, 
              role: 'assistant', 
              text: `Erro ao transcrever áudio: ${error.message}`, 
              createdAt: new Date().toISOString() 
            };
            setMessages(prev => [...prev, errMsg]);
          } finally {
            setIsTranscribing(false);
          }
        });
        stopRecordingRef.current = stopFn;
      } catch (error: any) {
        setIsRecording(false);
        const errMsg: Message = { 
          id: `e-${Date.now()}`, 
          role: 'assistant', 
          text: error.message, 
          createdAt: new Date().toISOString() 
        };
        setMessages(prev => [...prev, errMsg]);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    const validFiles = files.filter(file => {
      if (!isFileSupported(file)) {
        const errMsg: Message = { 
          id: `e-${Date.now()}`, 
          role: 'assistant', 
          text: `${file.name}: ${getFileErrorMessage(file)}`, 
          createdAt: new Date().toISOString() 
        };
        setMessages(prev => [...prev, errMsg]);
        return false;
      }
      return true;
    });

    setAttachedFiles(prev => [...prev, ...validFiles].slice(0, 1)); // Apenas 1 arquivo por vez
    
    // Limpar input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob && isFileSupported(blob)) {
          setAttachedFiles(prev => [...prev, blob].slice(0, 1));
        } else if (blob) {
          const errMsg: Message = {
            id: `e-${Date.now()}`,
            role: 'assistant',
            text: getFileErrorMessage(blob),
            createdAt: new Date().toISOString()
          };
          setMessages(prev => [...prev, errMsg]);
        }
        break;
      }
    }
  };

  const { mode } = useTheme();

  const isLight = mode === 'light';

  return (
    <div className={`min-h-screen flex flex-col items-center p-6 ${isLight ? 'bg-slate-50' : 'bg-slate-900'}`}>
      <div className="max-w-4xl w-full">
        {/* Título central atualizado */}
        <div className="text-center mb-8">
          <div className="inline-block bg-gradient-to-r from-emerald-500/10 to-indigo-500/10 border border-emerald-500/30 rounded-full px-6 py-3 mb-4">
            <div className="flex items-center gap-2 justify-center">
              <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse"></div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-emerald-300 bg-clip-text text-transparent">
                Bia, sua Assistente com IA
              </h1>
              <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse"></div>
            </div>
          </div>

          <p className="text-gray-300 text-lg font-medium max-w-2xl mx-auto leading-relaxed">
            Converse por voz em tempo real com a Bia e obtenha recomendações financeiras práticas, planejamento personalizado e insights acionáveis.
          </p>
        </div>

        <div className={`${isLight ? 'bg-white shadow-sm' : 'bg-slate-800 shadow-lg'} rounded-lg p-4 lg:p-6`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <aside className={`lg:col-span-3 rounded-md border p-3 ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-slate-900 border-slate-700'}`}>
              <button
                onClick={createNewConversation}
                className="w-full mb-3 bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                + Nova conversa
              </button>

              <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                {[...conversations]
                  .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                  .map((conversation) => {
                    const isActive = conversation.id === activeConversationId;
                    return (
                      <div
                        key={conversation.id}
                        className={`relative w-full text-left px-3 py-2 rounded-md border transition-colors ${
                          isActive
                            ? isLight
                              ? 'bg-emerald-100 border-emerald-300 text-slate-900'
                              : 'bg-emerald-500/20 border-emerald-500/50 text-white'
                            : isLight
                            ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                            : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        <button
                          onClick={() => selectConversation(conversation.id)}
                          className="w-full text-left pr-8"
                        >
                          <div className="text-sm font-medium truncate">{conversation.title}</div>
                          <div className={`text-xs mt-1 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                            {new Date(conversation.updatedAt).toLocaleString('pt-BR')}
                          </div>
                        </button>

                        <button
                          onClick={() => deleteConversation(conversation.id)}
                          className={`absolute top-2 right-2 p-1 rounded transition-colors ${
                            isLight
                              ? 'text-slate-500 hover:text-red-600 hover:bg-slate-200'
                              : 'text-slate-400 hover:text-red-400 hover:bg-slate-700'
                          }`}
                          title="Excluir conversa"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
              </div>
            </aside>

            <div className="lg:col-span-9">
          <div ref={scrollRef} className={`h-96 overflow-y-auto mb-4 p-3 rounded-md border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-slate-900 border-slate-800'}`}>
            {messages.map(m => (
              <div key={m.id} className={`mb-3 ${m.role === 'user' ? 'text-right' : 'text-left'}`}>
                <div className={`inline-block max-w-[80%] px-4 py-2 rounded-md ${m.role === 'user' ? 'bg-emerald-600 text-white' : (isLight ? 'bg-slate-100 text-slate-900' : 'bg-slate-700 text-gray-200')}`}>
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="mb-2 pb-2 border-b border-white/20">
                      {m.attachments.map((att, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs">
                          <FileImage size={14} />
                          <span>{att.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
                  <div className={`${isLight ? 'text-slate-500' : 'text-slate-400'} text-xs mt-1`}>{new Date(m.createdAt).toLocaleTimeString()}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Preview de arquivos anexados */}
          {attachedFiles.length > 0 && (
            <div className={`mb-3 p-3 rounded-md border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-slate-800 border-slate-700'}`}>
              <div className="flex flex-wrap gap-2">
                {attachedFiles.map((file, idx) => (
                  <div key={idx} className={`flex items-center gap-2 px-3 py-2 rounded-md ${isLight ? 'bg-white' : 'bg-slate-700'}`}>
                    <FileImage size={16} className="text-emerald-500" />
                    <span className="text-sm">{file.name}</span>
                    <button
                      onClick={() => removeFile(idx)}
                      className="ml-2 text-red-500 hover:text-red-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {/* Textarea para mensagem */}
            <div className="relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={
                  isTranscribing 
                    ? 'Transcrevendo áudio...' 
                    : loading 
                    ? 'Aguardando resposta...' 
                    : 'Escreva ou cole uma imagem (Ctrl+V)... Ctrl+Enter para enviar'
                }
                disabled={loading || !userEmail || isTranscribing}
                rows={4}
                className={`w-full px-4 py-3 rounded-md border focus:outline-none disabled:opacity-50 resize-none ${isLight ? 'bg-white text-slate-900 placeholder-slate-400 border-slate-200' : 'bg-slate-900 text-white placeholder-slate-500 border-slate-700'}`}
              />
            </div>

            {/* Botões de ação */}
            <div className="flex gap-2 items-center">
              {/* Input de arquivo (oculto) */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                multiple={false}
              />

              {/* Botão Anexar Arquivo */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || !userEmail || attachedFiles.length >= 1}
                className={`p-3 rounded-md transition-colors disabled:opacity-50 ${isLight ? 'bg-slate-200 hover:bg-slate-300 text-slate-700' : 'bg-slate-700 hover:bg-slate-600 text-white'}`}
                title="Anexar comprovante"
              >
                <Paperclip size={20} />
              </button>

              {/* Botão Microfone */}
              <button
                onClick={toggleRecording}
                disabled={loading || !userEmail || isTranscribing}
                className={`p-3 rounded-md transition-all disabled:opacity-50 ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
                    : isLight 
                    ? 'bg-slate-200 hover:bg-slate-300 text-slate-700' 
                    : 'bg-slate-700 hover:bg-slate-600 text-white'
                }`}
                title={isRecording ? 'Parar gravação' : 'Gravar áudio'}
              >
                {isRecording ? <MicOff size={20} /> : <Mic size={20} />}
              </button>

              {/* Botão Enviar */}
              <button
                onClick={sendMessage}
                disabled={loading || !userEmail || isTranscribing || processingFile || (!input.trim() && attachedFiles.length === 0)}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-emerald-500 text-white px-4 py-3 rounded-md shadow-md disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {processingFile ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Processando arquivo...</span>
                  </>
                ) : loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <span>Enviar</span>
                )}
              </button>

              {/* Botão Upgrade */}
              <a
                href="https://wa.me/5521968215101?text=Olá!%20Gostaria%20de%20saber%20mais%20sobre%20os%20planos%20do%20Grana%20em%20Dia"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-yellow-600 hover:bg-yellow-500 text-black px-4 py-3 rounded-md shadow-md whitespace-nowrap"
                title="Fazer upgrade"
              >
                Upgrade
              </a>
            </div>
          </div>

            <div className={`mt-4 text-sm ${isLight ? 'text-slate-700' : 'text-slate-400'}`}>
            <strong>Status:</strong> {userEmail ? `Conectado como ${userEmail}` : 'Carregando dados...'}
          </div>

          {/* CTA movida para baixo do status */}
          <div className={`mt-6 flex items-center justify-between gap-4 p-4 rounded-md border ${isLight ? 'bg-white/60 border-slate-200' : 'bg-slate-700/50 border-slate-600'}`}>
            <p className={`${isLight ? 'text-slate-700' : 'text-slate-300'} flex-1`}>
              Fale com a Bia por voz e receba dicas práticas, planejamento financeiro personalizado e orientações que realmente funcionam para você.
            </p>
            <a
              href="https://wa.me/5521968215101?text=Olá!%20Gostaria%20de%20saber%20mais%20sobre%20os%20planos%20do%20Grana%20em%20Dia"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-emerald-500 hover:bg-emerald-400 text-white px-4 py-2 rounded-md shadow-md whitespace-nowrap"
              title="Experimente agora"
            >
              Experimente agora!
            </a>
          </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistenteIA;